import { useEffect, useRef, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { API } from "@/lib/api";
import MarkdownView from "@/components/MarkdownView";
import { toast } from "sonner";
import { MessageCircle, X, Send, Plus, Sparkles, Mic, Square, Volume2, Loader2 } from "lucide-react";

const SID_KEY = "sphx_assistant_sid";
const newSid = () => `asst_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
const authHeaders = () => (localStorage.getItem("cs_token") ? { Authorization: `Bearer ${localStorage.getItem("cs_token")}` } : {});

const SUGGESTIONS = [
  "Plan a 60-minute practice for a U12 team",
  "How do I boost attendance at training?",
  "Write a friendly reminder to parents about fees",
];

export default function AssistantWidget() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [sid, setSid] = useState(() => localStorage.getItem(SID_KEY) || newSid());
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const [speakingIdx, setSpeakingIdx] = useState(null); // index being loaded/played
  const scrollRef = useRef(null);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const audioRef = useRef(null);

  useEffect(() => { localStorage.setItem(SID_KEY, sid); }, [sid]);

  useEffect(() => {
    if (!open || !user) return;
    let cancelled = false;
    fetch(`${API}/assistant/sessions/${sid}/messages`, { headers: authHeaders(), credentials: "include" })
      .then((r) => (r.ok ? r.json() : []))
      .then((d) => { if (!cancelled) setMessages((m) => (m.length ? m : (d || []))); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [open, sid, user]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, streaming]);

  // Stop any audio when the panel closes
  useEffect(() => {
    if (!open && audioRef.current) { audioRef.current.pause(); audioRef.current = null; setSpeakingIdx(null); }
  }, [open]);

  if (!user) return null;

  const newChat = () => { setSid(newSid()); setMessages([]); setInput(""); };

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || streaming) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: msg }, { role: "assistant", content: "" }]);
    setStreaming(true);
    try {
      const res = await fetch(`${API}/assistant/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        credentials: "include",
        body: JSON.stringify({ session_id: sid, message: msg }),
      });
      if (!res.ok || !res.body) throw new Error();
      const reader = res.body.getReader();
      const dec = new TextDecoder();
      let buf = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        const parts = buf.split("\n\n");
        buf = parts.pop();
        for (const p of parts) {
          if (!p.startsWith("data: ")) continue;
          const data = JSON.parse(p.slice(6));
          if (data.delta) setMessages((m) => {
            const c = [...m];
            const last = c[c.length - 1];
            if (!last || last.role !== "assistant") return [...c, { role: "assistant", content: data.delta }];
            c[c.length - 1] = { ...last, content: (last.content || "") + data.delta };
            return c;
          });
        }
      }
    } catch {
      setMessages((m) => {
        const c = [...m];
        c[c.length - 1] = { role: "assistant", content: "Sorry, something went wrong. Please try again." };
        return c;
      });
    } finally {
      setStreaming(false);
    }
  };

  const toggleRecord = async () => {
    if (recording) { recorderRef.current?.stop(); return; }
    if (streaming || transcribing) return;
    if (typeof MediaRecorder === "undefined" || !navigator.mediaDevices?.getUserMedia) {
      toast.error("Voice input isn't supported on this browser.");
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const rec = new MediaRecorder(stream);
      recorderRef.current = rec;
      chunksRef.current = [];
      rec.ondataavailable = (e) => { if (e.data.size) chunksRef.current.push(e.data); };
      rec.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        setRecording(false);
        const mime = rec.mimeType || "audio/webm";
        const ext = mime.includes("mp4") ? "mp4" : mime.includes("ogg") ? "ogg" : "webm";
        const blob = new Blob(chunksRef.current, { type: mime });
        if (!blob.size) return;
        setTranscribing(true);
        try {
          const fd = new FormData();
          fd.append("file", blob, `voice.${ext}`);
          const r = await fetch(`${API}/assistant/transcribe`, { method: "POST", headers: authHeaders(), credentials: "include", body: fd });
          if (!r.ok) throw new Error();
          const d = await r.json();
          const text = (d.text || "").trim();
          if (text) send(text);
          else toast.error("Didn't catch that — please try again.");
        } catch { toast.error("Couldn't transcribe your voice. Please try again."); }
        finally { setTranscribing(false); }
      };
      rec.start();
      setRecording(true);
    } catch { setRecording(false); toast.error("Microphone access was blocked. Enable it to use voice."); }
  };

  const speak = async (idx, text) => {
    if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; }
    if (speakingIdx === idx) { setSpeakingIdx(null); return; }
    setSpeakingIdx(idx);
    try {
      const r = await fetch(`${API}/assistant/speak`, {
        method: "POST", headers: { "Content-Type": "application/json", ...authHeaders() },
        credentials: "include", body: JSON.stringify({ text }),
      });
      const d = await r.json();
      if (!d.audio) throw new Error();
      const audio = new Audio(`data:${d.mime || "audio/mpeg"};base64,${d.audio}`);
      audioRef.current = audio;
      audio.onended = () => { setSpeakingIdx(null); audioRef.current = null; };
      audio.onerror = () => { setSpeakingIdx(null); audioRef.current = null; };
      await audio.play();
    } catch { setSpeakingIdx(null); audioRef.current = null; toast.error("Couldn't play audio. Please try again."); }
  };

  const onKey = (e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } };

  return (
    <>
      {!open && (
        <button
          onClick={() => setOpen(true)}
          data-testid="assistant-open-btn"
          aria-label="Ask AI"
          className="fixed z-50 right-4 bottom-20 lg:right-6 lg:bottom-6 h-14 w-14 rounded-full brand-gradient text-white shadow-xl flex items-center justify-center hover:scale-105 active:scale-95 transition-transform"
        >
          <MessageCircle size={24} />
        </button>
      )}

      {open && (
        <div
          data-testid="assistant-panel"
          className="fixed z-50 right-3 bottom-20 lg:right-6 lg:bottom-6 w-[calc(100vw-1.5rem)] sm:w-[400px] h-[70vh] max-h-[640px] rounded-2xl border border-border bg-card shadow-2xl flex flex-col overflow-hidden animate-fade-up"
        >
          <div className="flex items-center justify-between gap-2 px-4 py-3 border-b border-border brand-gradient text-white shrink-0">
            <div className="flex items-center gap-2">
              <Sparkles size={18} />
              <div className="leading-tight">
                <p className="font-heading font-semibold text-sm">SphereHyx Assistant</p>
                <p className="text-[11px] opacity-80">Powered by GPT-5.6 Terra</p>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <button onClick={newChat} data-testid="assistant-new-chat" aria-label="New chat" className="h-8 w-8 rounded-lg hover:bg-white/15 flex items-center justify-center"><Plus size={16} /></button>
              <button onClick={() => setOpen(false)} data-testid="assistant-close-btn" aria-label="Close" className="h-8 w-8 rounded-lg hover:bg-white/15 flex items-center justify-center"><X size={16} /></button>
            </div>
          </div>

          <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-4" data-testid="assistant-messages">
            {messages.length === 0 && (
              <div className="text-center mt-6">
                <div className="mx-auto h-12 w-12 rounded-full bg-primary/10 text-primary flex items-center justify-center mb-3"><Sparkles size={22} /></div>
                <p className="font-heading font-semibold">Hi, how can I help?</p>
                <p className="text-sm text-muted-foreground mt-1 mb-4">Ask me anything about coaching, your club, or SphereHyx. You can type or tap the mic to speak.</p>
                <div className="space-y-2">
                  {SUGGESTIONS.map((s, i) => (
                    <button key={i} onClick={() => send(s)} data-testid={`assistant-suggestion-${i}`} className="w-full text-left text-sm rounded-xl border border-border px-3 py-2 hover:bg-accent transition-colors">{s}</button>
                  ))}
                </div>
              </div>
            )}
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`} data-testid={`assistant-msg-${m.role}`}>
                <div className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 text-sm ${m.role === "user" ? "bg-primary text-primary-foreground" : "bg-muted text-foreground"}`}>
                  {m.role === "assistant"
                    ? (m.content
                      ? <div>
                          <div className="prose prose-sm prose-invert max-w-none [&_p]:my-1 [&_ul]:my-1 [&_h2]:text-base [&_h3]:text-sm"><MarkdownView>{m.content}</MarkdownView></div>
                          <button onClick={() => speak(i, m.content)} data-testid={`assistant-speak-${i}`} aria-label="Read aloud" className="mt-1.5 inline-flex items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground transition-colors">
                            {speakingIdx === i ? <Loader2 size={13} className="animate-spin" /> : <Volume2 size={13} />}
                            {speakingIdx === i ? "Playing…" : "Listen"}
                          </button>
                        </div>
                      : <span className="inline-flex gap-1 items-center text-muted-foreground"><span className="h-2 w-2 bg-current rounded-full animate-pulse" /> Thinking…</span>)
                    : <span className="whitespace-pre-wrap">{m.content}</span>}
                </div>
              </div>
            ))}
          </div>

          <div className="border-t border-border p-3 shrink-0">
            <div className="flex items-end gap-2">
              <button
                onClick={toggleRecord}
                disabled={streaming || transcribing}
                data-testid="assistant-mic-btn"
                aria-label={recording ? "Stop recording" : "Record voice"}
                className={`h-10 w-10 shrink-0 rounded-xl flex items-center justify-center transition-colors disabled:opacity-40 ${recording ? "bg-red-500 text-white animate-pulse" : "border border-border hover:bg-accent"}`}
              >
                {transcribing ? <Loader2 size={17} className="animate-spin" /> : recording ? <Square size={16} /> : <Mic size={17} />}
              </button>
              <textarea
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={onKey}
                rows={1}
                placeholder={recording ? "Listening…" : transcribing ? "Transcribing…" : "Ask anything…"}
                data-testid="assistant-input"
                className="flex-1 resize-none max-h-28 rounded-xl border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
              />
              <button onClick={() => send()} disabled={streaming || !input.trim()} data-testid="assistant-send-btn" aria-label="Send" className="h-10 w-10 shrink-0 rounded-xl brand-gradient text-white flex items-center justify-center disabled:opacity-40">
                <Send size={17} />
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
